import { motion } from "framer-motion";
import { Award } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE } from "../lib/whatsapp";

const palaces = ["Hôtel Bristol", "Crillon", "Plaza Athénée", "Ritz", "Alain Ducasse", "Gordon Ramsay"];

export const MaitreHotel = () => (
  <section id="maitre" data-testid="maitre-section" className="grain relative bg-[#f9f8f6] px-6 py-28 text-[#0a0a0a] lg:px-10 lg:py-40">
    <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative mx-auto w-full"
      >
        <div className="absolute -inset-3 rounded-[2rem] border border-[#aa8c2c]/25" />
        <div className="overflow-hidden rounded-3xl border border-[#0a0a0a]/10 shadow-2xl">
          <motion.img
            src="/images/maitre.png"
            alt="Maître d'hôtel derrière sa brigade dans une somptueuse salle de réception avec mise en place"
            data-testid="maitre-photo"
            animate={{ scale: [1, 1.07, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
        <div className="absolute bottom-5 left-1/2 w-max -translate-x-1/2 rounded-full border border-white/15 bg-[#0a0a0a]/80 px-6 py-2.5 backdrop-blur-xl" data-testid="maitre-photo-caption">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Votre maître d'hôtel — Fly</p>
        </div>
      </motion.div>
      <div>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-6 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#aa8c2c]"
        >
          Votre maître d'hôtel
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="font-display text-4xl leading-tight tracking-tight text-[#0a0a0a] sm:text-5xl lg:text-6xl"
        >
          L'excellence des palaces parisiens, <em className="italic text-[#aa8c2c]">à votre service.</em>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#0a0a0a]/60"
        >
          Après plusieurs années comme maître d'hôtel dans les plus grands palaces parisiens
          et aux côtés des chefs les plus étoilés — <strong className="text-[#0a0a0a]">Alain Ducasse</strong> et{" "}
          <strong className="text-[#0a0a0a]">Gordon Ramsay</strong> — je vous offre la même qualité de service
          haut de gamme, avec une attention particulière portée à chaque client et un service entièrement
          personnalisé. Une organisation millimétrée, à la carte et de proximité, pensée pour votre satisfaction.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-8"
          data-testid="maitre-name"
        >
          <p className="font-display text-4xl italic text-gold">Fly</p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
            Maître d'hôtel — Fondateur de FK Conseil &amp; Événement
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          {palaces.map((p) => (
            <span
              key={p}
              data-testid={`palace-chip-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="flex items-center gap-2 rounded-full border border-[#aa8c2c]/40 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#aa8c2c]"
            >
              <Award className="h-3.5 w-3.5" />
              {p}
            </span>
          ))}
        </motion.div>
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.4 }}
          href={waLink(DEFAULT_WA_MESSAGE)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="maitre-whatsapp-cta"
          className="mt-12 inline-block rounded-full bg-gold px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03]"
        >
          Réserver votre maître d'hôtel
        </motion.a>
      </div>
    </div>
  </section>
);
